import React, { useState, useEffect } from 'react';
import {
  getConfiguracion,
  getPublicConfiguracion,
  getMenuRango,
  crearPedido,
  crearPedidosMultiples,
  getPedidosUsuario,
  cancelarPedido,
} from '../../services/api';
import { cerrarSesion } from '../../services/sessionManager';
import Logo from '../../components/Logo';
import PerfilUsuario from '../../components/PerfilUsuario';

function Dashboard({ usuario, onLogout }) {
  const [activeTab, setActiveTab] = useState('nuevo-pedido');
  const [config, setConfig] = useState(null);
  const [fechasPeriodo, setFechasPeriodo] = useState([]);
  const [menusPorFecha, setMenusPorFecha] = useState({});
  const [seleccionesPorFecha, setSeleccionesPorFecha] = useState({}); // Para encargados: {fecha: {categoriaKey: {plato_id, cantidad}}}
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [showPerfil, setShowPerfil] = useState(false);
  
  const esEncargado = usuario.tipo_usuario === 'encargado';

  useEffect(() => {
    cargarDatos();
  }, [usuario.dni]);

  const getFechaLocal = (diasAdelante = 0) => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() + diasAdelante);
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calcularFechasPeriodo = (tipoPeriodo, diasAnticipacion) => {
    const fechas = [];
    const inicio = parseInt(diasAnticipacion) || 1;
    const hoy = new Date();

    if (tipoPeriodo === 'diario') {
      fechas.push(getFechaLocal(inicio));
    } else if (tipoPeriodo === 'semanal') {
      // Mostrar siempre la próxima semana completa (lunes a domingo)
      const fechaInicio = new Date();
      fechaInicio.setDate(hoy.getDate() + inicio);
      
      // Calcular el próximo lunes desde la fecha de inicio
      const diaSemana = fechaInicio.getDay(); // 0 = domingo, 1 = lunes, ..., 6 = sábado
      let diasHastaLunes = 0;
      
      if (diaSemana === 0) {
        // Si es domingo, el lunes es mañana
        diasHastaLunes = 1;
      } else if (diaSemana === 1) {
        // Si ya es lunes, usar ese mismo día
        diasHastaLunes = 0;
      } else {
        // Si es martes a sábado, calcular días hasta el próximo lunes
        diasHastaLunes = 8 - diaSemana;
      }
      
      // Calcular desde qué día empezar (el lunes de la próxima semana)
      const diasDesdeHoy = inicio + diasHastaLunes;
      
      // Generar 7 días desde el lunes al domingo
      for (let i = 0; i < 7; i++) {
        fechas.push(getFechaLocal(diasDesdeHoy + i));
      }
    } else if (tipoPeriodo === 'quincenal') {
      // 15 días desde mañana
      const diaInicio = inicio;
      const diaFin = diaInicio + 14; // 15 días totales

      for (let i = diaInicio; i <= diaFin; i++) {
        fechas.push(getFechaLocal(i));
      }
    } else if (tipoPeriodo === 'mensual') {
      // Desde mañana hasta el final del mes
      const hoy = new Date();
      const mañana = new Date(hoy);
      mañana.setDate(hoy.getDate() + parseInt(diasAnticipacion));
      
      const ultimoDiaMes = new Date(mañana.getFullYear(), mañana.getMonth() + 1, 0);

      for (let d = new Date(mañana); d <= ultimoDiaMes; d.setDate(d.getDate() + 1)) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        fechas.push(`${year}-${month}-${day}`);
      }
    }

    return fechas;
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar configuración
      let configArray;
      try {
        // Si es super_admin, obtener configuración completa
        if (usuario.tipo_usuario === 'super_admin') {
          const configRes = await getConfiguracion();
          configArray = configRes.data;
        } else {
          // Usuarios normales solo obtienen las claves públicas necesarias
          const configRes = await getPublicConfiguracion();
          configArray = configRes.data;
        }
      } catch (err) {
        console.error('Error al obtener configuración:', err);
        throw err; // dejar que el catch externo muestre el error genérico
      }

      const configObj = {};
      if (Array.isArray(configArray)) {
        configArray.forEach(item => {
          configObj[item.clave] = item.valor;
        });
      } else if (configArray && typeof configArray === 'object') {
        // Por si el backend retorna objeto
        Object.keys(configArray).forEach(k => {
          configObj[k] = configArray[k];
        });
      }

      setConfig(configObj);

      const tipoPeriodo = configObj.tipo_periodo_pedido || 'diario';
      const diasAnticipacion = configObj.dias_anticipacion_pedido || '1';

      // Calcular fechas del período
      const fechas = calcularFechasPeriodo(tipoPeriodo, diasAnticipacion);
      setFechasPeriodo(fechas);

      if (fechas.length > 0) {
        setFechaSeleccionada(fechas[0]);

        // Cargar menú para el rango de fechas (almuerzo y cena)
        const fechaInicio = fechas[0];
        const fechaFin = fechas[fechas.length - 1];

        const sedeId = usuario.sede?.id || 1;
        
        // Cargar ambos tipos de comida
        const [menuAlmuerzoRes, menuCenaRes] = await Promise.all([
          getMenuRango(fechaInicio, fechaFin, sedeId, 'almuerzo'),
          getMenuRango(fechaInicio, fechaFin, sedeId, 'cena')
        ]);

        // Organizar menú por fecha y tipo de comida
        const menuOrganizado = {};
        fechas.forEach(fecha => {
          menuOrganizado[fecha] = {
            almuerzo: [],
            cena: []
          };
        });

        // Procesar menús de almuerzo
        if (menuAlmuerzoRes.data && Array.isArray(menuAlmuerzoRes.data)) {
          menuAlmuerzoRes.data.forEach(menuDia => {
            const fecha = menuDia.fecha;
            if (menuOrganizado[fecha]) {
              let categoria = menuOrganizado[fecha].almuerzo.find(c => c.categoria_id === menuDia.categoria_id);
              if (!categoria) {
                categoria = {
                  categoria_id: menuDia.categoria_id,
                  categoria: menuDia.categoria,
                  orden: menuDia.orden,
                  tipo_comida: 'almuerzo',
                  platos: []
                };
                menuOrganizado[fecha].almuerzo.push(categoria);
              }

              // Evitar duplicados
              const platoExiste = categoria.platos.some(p => p.id === menuDia.plato_id);
              if (!platoExiste) {
                categoria.platos.push({
                  id: menuDia.plato_id,
                  nombre: menuDia.plato,
                  descripcion: menuDia.descripcion,
                  imagen_url: menuDia.imagen_url
                });
              }
            }
          });
        }

        // Procesar menús de cena
        if (menuCenaRes.data && Array.isArray(menuCenaRes.data)) {
          menuCenaRes.data.forEach(menuDia => {
            const fecha = menuDia.fecha;
            if (menuOrganizado[fecha]) {
              let categoria = menuOrganizado[fecha].cena.find(c => c.categoria_id === menuDia.categoria_id);
              if (!categoria) {
                categoria = {
                  categoria_id: menuDia.categoria_id,
                  categoria: menuDia.categoria,
                  orden: menuDia.orden,
                  tipo_comida: 'cena',
                  platos: []
                };
                menuOrganizado[fecha].cena.push(categoria);
              }

              // Evitar duplicados
              const platoExiste = categoria.platos.some(p => p.id === menuDia.plato_id);
              if (!platoExiste) {
                categoria.platos.push({
                  id: menuDia.plato_id,
                  nombre: menuDia.plato,
                  descripcion: menuDia.descripcion,
                  imagen_url: menuDia.imagen_url
                });
              }
            }
          });
        }

        // Ordenar categorías por orden
        Object.keys(menuOrganizado).forEach(fecha => {
          menuOrganizado[fecha].almuerzo.sort((a, b) => a.orden - b.orden);
          menuOrganizado[fecha].cena.sort((a, b) => a.orden - b.orden);
        });

        setMenusPorFecha(menuOrganizado);
      }

      // Cargar historial de pedidos
      const historialRes = await getPedidosUsuario(usuario.dni);
      setPedidos(historialRes.data);

    } catch (error) {
      console.error('Error cargando datos:', error);
      mostrarMensaje('Error al cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSeleccion = (fecha, categoriaId, platoId) => {
    setSeleccionesPorFecha(prev => {
      const seleccionesDelDia = prev[fecha] || {};
      const seleccionCategoria = seleccionesDelDia[categoriaId];

      if (esEncargado) {
        // Para encargados: array de platos con cantidad
        const platosArray = Array.isArray(seleccionCategoria) ? seleccionCategoria : [];
        const platoIndex = platosArray.findIndex(p => p.plato_id === platoId);

        if (platoIndex >= 0) {
          // Si ya está seleccionado, lo quitamos
          const nuevoArray = platosArray.filter(p => p.plato_id !== platoId);
          
          if (nuevoArray.length === 0) {
            // Si no quedan platos, eliminamos la categoría
            const nuevasSelecciones = { ...seleccionesDelDia };
            delete nuevasSelecciones[categoriaId];
            return {
              ...prev,
              [fecha]: nuevasSelecciones
            };
          }

          return {
            ...prev,
            [fecha]: {
              ...seleccionesDelDia,
              [categoriaId]: nuevoArray
            }
          };
        } else {
          // Si no está seleccionado, lo agregamos
          return {
            ...prev,
            [fecha]: {
              ...seleccionesDelDia,
              [categoriaId]: [...platosArray, { plato_id: platoId, cantidad: 1 }]
            }
          };
        }
      } else {
        // Para usuarios normales: un solo plato por categoría
        const platoSeleccionado = seleccionCategoria;

        // Si el plato ya está seleccionado, lo deseleccionamos
        if (platoSeleccionado === platoId) {
          const nuevasSelecciones = { ...seleccionesDelDia };
          delete nuevasSelecciones[categoriaId];
          return {
            ...prev,
            [fecha]: nuevasSelecciones
          };
        }

        // Si no está seleccionado, lo seleccionamos
        return {
          ...prev,
          [fecha]: {
            ...seleccionesDelDia,
            [categoriaId]: platoId
          }
        };
      }
    });
  };

  const actualizarCantidadPlato = (fecha, categoriaId, platoId, nuevaCantidad) => {
    setSeleccionesPorFecha(prev => {
      const seleccionesDelDia = prev[fecha] || {};
      const platosArray = seleccionesDelDia[categoriaId];

      if (!platosArray || !esEncargado || !Array.isArray(platosArray)) return prev;

      const nuevoArray = platosArray.map(p => 
        p.plato_id === platoId 
          ? { ...p, cantidad: parseInt(nuevaCantidad) || 1 }
          : p
      );

      return {
        ...prev,
        [fecha]: {
          ...seleccionesDelDia,
          [categoriaId]: nuevoArray
        }
      };
    });
  };

  const validarSelecciones = () => {
    // Validar que al menos un día tenga selecciones
    let hayAlgunaSeleccion = false;
    let hayAlgunDiaConMenu = false;

    for (const fecha of fechasPeriodo) {
      const menuDia = menusPorFecha[fecha] || { almuerzo: [], cena: [] };
      const selecciones = seleccionesPorFecha[fecha] || {};

      // Si este día NO tiene menú, lo ignoramos completamente (feriado, no laboral, etc.)
      const tieneMenu = (menuDia.almuerzo?.length > 0) || (menuDia.cena?.length > 0);
      if (!tieneMenu) {
        continue;
      }

      hayAlgunDiaConMenu = true;

      // Si este día tiene selecciones
      if (Object.keys(selecciones).length > 0) {
        hayAlgunaSeleccion = true;

        // Validar que si hay selecciones en almuerzo, al menos tenga un plato principal
        const categoriasAlmuerzo = menuDia.almuerzo || [];
        const tieneCategoriaPrincipalAlmuerzo = categoriasAlmuerzo.find(cat => cat.categoria === 'Principal');
        
        if (tieneCategoriaPrincipalAlmuerzo && !selecciones[`almuerzo-${tieneCategoriaPrincipalAlmuerzo.categoria_id}`]) {
          // Solo validar si hay alguna selección de almuerzo
          const haySeleccionAlmuerzo = Object.keys(selecciones).some(key => key.startsWith('almuerzo-'));
          if (haySeleccionAlmuerzo) {
            return `Si pedís almuerzo para ${formatearFecha(fecha)}, debes seleccionar un plato principal`;
          }
        }

        // Validar que si hay selecciones en cena, al menos tenga un plato principal
        const categoriasCena = menuDia.cena || [];
        const tieneCategoriaPrincipalCena = categoriasCena.find(cat => cat.categoria === 'Principal');
        
        if (tieneCategoriaPrincipalCena && !selecciones[`cena-${tieneCategoriaPrincipalCena.categoria_id}`]) {
          // Solo validar si hay alguna selección de cena
          const haySeleccionCena = Object.keys(selecciones).some(key => key.startsWith('cena-'));
          if (haySeleccionCena) {
            return `Si pedís cena para ${formatearFecha(fecha)}, debes seleccionar un plato principal`;
          }
        }
      }
    }

    // Si no hay ningún día con menú disponible
    if (!hayAlgunDiaConMenu) {
      return 'No hay menús disponibles para el período seleccionado';
    }

    // Validar que al menos haya un día con selecciones
    if (!hayAlgunaSeleccion) {
      return 'Debes seleccionar al menos un día para hacer tu pedido';
    }

    return null;
  };

  const handleCrearPedidos = async () => {
    const error = validarSelecciones();
    if (error) {
      mostrarMensaje(error, 'error');
      return;
    }

    const tipoPeriodo = config?.tipo_periodo_pedido || 'diario';

    // Contar solo los días con selecciones
    const diasConSelecciones = fechasPeriodo.filter(fecha => {
      const selecciones = seleccionesPorFecha[fecha] || {};
      return Object.keys(selecciones).length > 0;
    });

    const cantidadDias = diasConSelecciones.length;

    if (!window.confirm(`¿Confirmas tu pedido para ${cantidadDias} día(s)?`)) {
      return;
    }

    setLoading(true);
    try {
      // Para cada día, necesitamos separar selecciones de almuerzo y cena
      const pedidosPorTipoComida = { almuerzo: [], cena: [] };

      diasConSelecciones.forEach(fecha => {
        const selecciones = seleccionesPorFecha[fecha] || {};
        
        // Separar por tipo de comida
        const platosAlmuerzo = [];
        const platosCena = [];

        Object.entries(selecciones).forEach(([categoriaKey, platoData]) => {
          if (esEncargado && Array.isArray(platoData)) {
            // Para encargados: array de platos con cantidad
            platoData.forEach(plato => {
              const platoConCantidad = { 
                plato_id: plato.plato_id, 
                cantidad: plato.cantidad 
              };

              if (categoriaKey.startsWith('almuerzo-')) {
                platosAlmuerzo.push(platoConCantidad);
              } else if (categoriaKey.startsWith('cena-')) {
                platosCena.push(platoConCantidad);
              }
            });
          } else {
            // Para usuarios normales: un solo plato por categoría
            const platoId = platoData;
            const platoConCantidad = { plato_id: platoId, cantidad: 1 };

            if (categoriaKey.startsWith('almuerzo-')) {
              platosAlmuerzo.push(platoConCantidad);
            } else if (categoriaKey.startsWith('cena-')) {
              platosCena.push(platoConCantidad);
            }
          }
        });

        if (platosAlmuerzo.length > 0) {
          pedidosPorTipoComida.almuerzo.push({ fecha, platos: platosAlmuerzo });
        }
        if (platosCena.length > 0) {
          pedidosPorTipoComida.cena.push({ fecha, platos: platosCena });
        }
      });

      // Crear pedidos de almuerzo
      if (pedidosPorTipoComida.almuerzo.length > 0) {
        if (tipoPeriodo === 'diario' && pedidosPorTipoComida.almuerzo.length === 1) {
          const { fecha, platos } = pedidosPorTipoComida.almuerzo[0];
          await crearPedido({
            dni: usuario.dni,
            platos: platos,
            tipo_interfaz: 'web',
            tipo_comida: 'almuerzo'
          });
        } else {
          await crearPedidosMultiples({
            dni: usuario.dni,
            tipo_interfaz: 'web',
            tipo_comida: 'almuerzo',
            pedidos: pedidosPorTipoComida.almuerzo
          });
        }
      }

      // Crear pedidos de cena
      if (pedidosPorTipoComida.cena.length > 0) {
        if (tipoPeriodo === 'diario' && pedidosPorTipoComida.cena.length === 1) {
          const { fecha, platos } = pedidosPorTipoComida.cena[0];
          await crearPedido({
            dni: usuario.dni,
            platos: platos,
            tipo_interfaz: 'web',
            tipo_comida: 'cena'
          });
        } else {
          await crearPedidosMultiples({
            dni: usuario.dni,
            tipo_interfaz: 'web',
            tipo_comida: 'cena',
            pedidos: pedidosPorTipoComida.cena
          });
        }
      }

      mostrarMensaje(`Pedido(s) creado(s) exitosamente para ${cantidadDias} día(s)`, 'success');
      setSeleccionesPorFecha({});
      cargarDatos();
      setActiveTab('historial');
    } catch (error) {
      console.error('Error creando pedidos:', error);
      mostrarMensaje(
        error.response?.data?.error || 'Error al crear los pedidos',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarPedido = async (pedidoId) => {
    if (!window.confirm('¿Estás seguro de cancelar este pedido?')) {
      return;
    }

    try {
      await cancelarPedido(pedidoId, usuario.dni);
      mostrarMensaje('Pedido cancelado exitosamente', 'success');
      cargarDatos();
    } catch (error) {
      console.error('Error cancelando pedido:', error);
      mostrarMensaje(
        error.response?.data?.error || 'Error al cancelar el pedido',
        'error'
      );
    }
  };

  const mostrarMensaje = (texto, tipo) => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 5000);
  };

  const handleLogout = () => {
    cerrarSesion();
    onLogout();
  };

  const formatearFecha = (fechaStr) => {
    const [year, month, day] = fechaStr.split('-');
    const fecha = new Date(year, month - 1, day);
    return fecha.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatearFechaCompleta = (fechaStr) => {
    const [year, month, day] = fechaStr.split('-');
    const fecha = new Date(year, month - 1, day);
    return fecha.toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const contarSeleccionesParaFecha = (fecha) => {
    return Object.keys(seleccionesPorFecha[fecha] || {}).length;
  };

  const tipoPeriodo = config?.tipo_periodo_pedido || 'diario';
  const titulosPeriodo = {
    diario: 'Pedido para Mañana',
    semanal: 'Pedido Semanal',
    quincenal: 'Pedido Quincenal',
    mensual: 'Pedido Mensual'
  };

  if (loading && !config) {
    return (
      <div className="container">
        <div className="loading">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{
      background: '#F5F5F5',
      minHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      margin: 0,
      padding: '20px',
      paddingBottom: '100px',
      position: 'relative',
      left: '50%',
      right: '50%',
      marginLeft: '-50vw',
      marginRight: '-50vw'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      <div className="header" style={{
        background: `
          radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35), transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(0,0,0,0.28), transparent 55%),
          linear-gradient(135deg, #1fa55a, #178e4d)
        `,
        padding: '20px 30px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)'
      }}>
        <Logo size="small" showText={true} textColor="white" style={{ 
          justifyContent: 'flex-start'
        }} />
        <div className="user-info">
          <div className="user-badge" style={{
            background: 'rgba(255, 255, 255, 0.2)',
            padding: '8px 16px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 500,
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            {usuario.nombreCompleto}
          </div>
          <button 
            className="btn" 
            onClick={() => setShowPerfil(true)} 
            style={{ 
              width: 'auto', 
              padding: '10px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              background: '#FB8C00',
              color: '#fff',
              border: 'none',
              marginRight: '8px'
            }}
          >
            👤 Perfil
          </button>
          <button className="btn btn-secondary" onClick={handleLogout} style={{ 
            width: 'auto', 
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 600
          }}>
            🚪 Salir
          </button>
        </div>
      </div>

      {mensaje && <div className={`alert alert-${mensaje.tipo}`}>{mensaje.texto}</div>}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'nuevo-pedido' ? 'active' : ''}`}
          onClick={() => setActiveTab('nuevo-pedido')}
        >
          Nuevo Pedido
        </button>
        <button
          className={`tab ${activeTab === 'historial' ? 'active' : ''}`}
          onClick={() => setActiveTab('historial')}
        >
          Mis Pedidos
        </button>
      </div>

      {activeTab === 'nuevo-pedido' && (
        <div className="card">
          <h2>{titulosPeriodo[tipoPeriodo]}</h2>
          <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
            {tipoPeriodo === 'diario'
              ? 'Selecciona los platos que deseas pedir para mañana'
              : `Selecciona los platos para ${fechasPeriodo.length} días`}
          </p>

          {loading && <div className="loading">Cargando menú...</div>}

          {!loading && fechasPeriodo.length === 0 && (
            <div className="empty-state">
              <h3>No hay fechas disponibles</h3>
              <p>No se pudo calcular el período de pedidos</p>
            </div>
          )}

          {!loading && fechasPeriodo.length > 0 && (
            <>
              {/* Selector de fechas (solo si no es diario) */}
              {tipoPeriodo !== 'diario' && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '10px', fontSize: '18px' }}>Selecciona el día:</h3>
                  <p style={{ marginBottom: '15px', fontSize: '14px', color: '#7f8c8d', fontStyle: 'italic' }}>
                    💡 Podés elegir para qué días querés pedir. Los días sin menú disponible (feriados o no laborables) aparecen deshabilitados.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {fechasPeriodo.map(fecha => {
                      const selecciones = contarSeleccionesParaFecha(fecha);
                      const menuDia = menusPorFecha[fecha] || { almuerzo: [], cena: [] };
                      const tieneMenu = (menuDia.almuerzo?.length > 0) || (menuDia.cena?.length > 0);

                      return (
                        <button
                          key={fecha}
                          onClick={() => setFechaSeleccionada(fecha)}
                          style={{
                            padding: '12px 20px',
                            border: fechaSeleccionada === fecha ? '2px solid #27AE60' : '2px solid #E0E0E0',
                            borderRadius: '8px',
                            background: !tieneMenu ? '#F5F5F5' : fechaSeleccionada === fecha ? '#FFEBEE' : 'white',
                            cursor: tieneMenu ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: fechaSeleccionada === fecha ? 600 : 400,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '5px',
                            minWidth: '120px',
                            opacity: tieneMenu ? 1 : 0.6,
                            color: fechaSeleccionada === fecha ? '#27AE60' : '#212121',
                            transition: 'all 0.25s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (tieneMenu) {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(39, 174, 96, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <span>{formatearFecha(fecha)}</span>
                          {!tieneMenu && (
                            <span style={{ fontSize: '11px', color: '#9E9E9E' }}>
                              Sin menú
                            </span>
                          )}
                          {tieneMenu && selecciones > 0 && (
                            <span style={{ fontSize: '12px', color: '#4CAF50', fontWeight: 600 }}>
                              ✓ {selecciones} plato{selecciones > 1 ? 's' : ''}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Menú del día seleccionado */}
              <div>
                <h3 style={{ marginBottom: '20px', fontSize: '20px', color: '#2c3e50' }}>
                  Menú para {formatearFechaCompleta(fechaSeleccionada || fechasPeriodo[0])}
                </h3>

                {(() => {
                  const menuDia = menusPorFecha[fechaSeleccionada || fechasPeriodo[0]];
                  const sinMenu = !menuDia || (menuDia.almuerzo?.length === 0 && menuDia.cena?.length === 0);

                  if (sinMenu) {
                    return (
                      <div className="empty-state">
                        <h3>No hay menú disponible</h3>
                        <p>Aún no se ha cargado el menú para este día</p>
                      </div>
                    );
                  }

                  const fechaActual = fechaSeleccionada || fechasPeriodo[0];
                  const seleccionesActuales = seleccionesPorFecha[fechaActual] || {};

                  return (
                    <>
                      {/* ALMUERZO */}
                      {menuDia.almuerzo && menuDia.almuerzo.length > 0 && (
                        <div style={{ marginBottom: '40px' }}>
                          <div style={{ 
                            background: `
                              radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35), transparent 50%),
                              radial-gradient(circle at 80% 70%, rgba(0,0,0,0.28), transparent 55%),
                              linear-gradient(135deg, #1fa55a, #178e4d)
                            `,
                            color: 'white',
                            padding: '15px 25px',
                            borderRadius: '10px',
                            marginBottom: '20px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}>
                            🌞 ALMUERZO
                          </div>
                          
                          {menuDia.almuerzo.map(categoria => (
                            <div key={`almuerzo-${categoria.categoria_id}`} className="menu-category">
                              <h3>
                                {categoria.categoria}
                                {seleccionesActuales[`almuerzo-${categoria.categoria_id}`] && (
                                  <span style={{ fontSize: '14px', color: '#27ae60', marginLeft: '10px' }}>
                                    {esEncargado && Array.isArray(seleccionesActuales[`almuerzo-${categoria.categoria_id}`])
                                      ? `✓ ${seleccionesActuales[`almuerzo-${categoria.categoria_id}`].length} seleccionado(s)`
                                      : '✓ Seleccionado'
                                    }
                                  </span>
                                )}
                              </h3>
                              <div className="menu-grid">
                                {categoria.platos.map(plato => {
                                  const seleccionCategoria = seleccionesActuales[`almuerzo-${categoria.categoria_id}`];
                                  
                                  let estaSeleccionado = false;
                                  let platoSeleccionado = null;

                                  if (esEncargado && Array.isArray(seleccionCategoria)) {
                                    platoSeleccionado = seleccionCategoria.find(p => p.plato_id === plato.id);
                                    estaSeleccionado = !!platoSeleccionado;
                                  } else {
                                    estaSeleccionado = seleccionCategoria === plato.id;
                                  }

                                  return (
                                    <div
                                      key={`almuerzo-${plato.id}`}
                                      className={`menu-item ${estaSeleccionado ? 'selected' : ''}`}
                                    >
                                      <div onClick={() => toggleSeleccion(fechaActual, `almuerzo-${categoria.categoria_id}`, plato.id)}>
                                        {plato.imagen_url && <img src={plato.imagen_url} alt={plato.nombre} />}
                                        <div className="menu-item-content">
                                          <h4>{plato.nombre}</h4>
                                          <p>{plato.descripcion}</p>
                                          {estaSeleccionado && (
                                            <span className="selected-badge">SELECCIONADO</span>
                                          )}
                                        </div>
                                      </div>
                                      {esEncargado && estaSeleccionado && (
                                        <div className="cantidad-selector" onClick={(e) => e.stopPropagation()}>
                                          <label>Cantidad:</label>
                                          <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={platoSeleccionado?.cantidad || 1}
                                            onChange={(e) => actualizarCantidadPlato(fechaActual, `almuerzo-${categoria.categoria_id}`, plato.id, e.target.value)}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* CENA */}
                      {menuDia.cena && menuDia.cena.length > 0 && (
                        <div>
                          <div style={{ 
                            background: `
                              radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35), transparent 50%),
                              radial-gradient(circle at 80% 70%, rgba(0,0,0,0.28), transparent 55%),
                              linear-gradient(135deg, #f093fb, #f5576c)
                            `,
                            color: 'white',
                            padding: '15px 25px',
                            borderRadius: '10px',
                            marginBottom: '20px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}>
                            🌙 CENA
                          </div>
                          
                          {menuDia.cena.map(categoria => (
                            <div key={`cena-${categoria.categoria_id}`} className="menu-category">
                              <h3>
                                {categoria.categoria}
                                {seleccionesActuales[`cena-${categoria.categoria_id}`] && (
                                  <span style={{ fontSize: '14px', color: '#27ae60', marginLeft: '10px' }}>
                                    {esEncargado && Array.isArray(seleccionesActuales[`cena-${categoria.categoria_id}`])
                                      ? `✓ ${seleccionesActuales[`cena-${categoria.categoria_id}`].length} seleccionado(s)`
                                      : '✓ Seleccionado'
                                    }
                                  </span>
                                )}
                              </h3>
                              <div className="menu-grid">
                                {categoria.platos.map(plato => {
                                  const seleccionCategoria = seleccionesActuales[`cena-${categoria.categoria_id}`];
                                  
                                  let estaSeleccionado = false;
                                  let platoSeleccionado = null;

                                  if (esEncargado && Array.isArray(seleccionCategoria)) {
                                    platoSeleccionado = seleccionCategoria.find(p => p.plato_id === plato.id);
                                    estaSeleccionado = !!platoSeleccionado;
                                  } else {
                                    estaSeleccionado = seleccionCategoria === plato.id;
                                  }

                                  return (
                                    <div
                                      key={`cena-${plato.id}`}
                                      className={`menu-item ${estaSeleccionado ? 'selected' : ''}`}
                                    >
                                      <div onClick={() => toggleSeleccion(fechaActual, `cena-${categoria.categoria_id}`, plato.id)}>
                                        {plato.imagen_url && <img src={plato.imagen_url} alt={plato.nombre} />}
                                        <div className="menu-item-content">
                                          <h4>{plato.nombre}</h4>
                                          <p>{plato.descripcion}</p>
                                          {estaSeleccionado && (
                                            <span className="selected-badge">SELECCIONADO</span>
                                          )}
                                        </div>
                                      </div>
                                      {esEncargado && estaSeleccionado && (
                                        <div className="cantidad-selector" onClick={(e) => e.stopPropagation()}>
                                          <label>Cantidad:</label>
                                          <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={platoSeleccionado?.cantidad || 1}
                                            onChange={(e) => actualizarCantidadPlato(fechaActual, `cena-${categoria.categoria_id}`, plato.id, e.target.value)}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Resumen y botón de confirmar */}
                <div style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <h3 style={{ marginBottom: '15px' }}>Resumen del Pedido</h3>

                  {fechasPeriodo.map(fecha => {
                    const selecciones = contarSeleccionesParaFecha(fecha);
                    if (selecciones === 0) return null;

                    return (
                      <div key={fecha} style={{ marginBottom: '10px', padding: '10px', background: 'white', borderRadius: '5px' }}>
                        <strong>{formatearFecha(fecha)}:</strong> {selecciones} plato{selecciones > 1 ? 's' : ''} seleccionado{selecciones > 1 ? 's' : ''}
                      </div>
                    );
                  })}

                  {/* Campo de cantidad para encargados */}
                  <button
                    className="btn btn-success"
                    onClick={handleCrearPedidos}
                    disabled={loading || Object.keys(seleccionesPorFecha).length === 0}
                    style={{ marginTop: '20px', width: '100%', padding: '15px', fontSize: '16px' }}
                  >
                    {loading ? 'Creando pedido(s)...' : 
                      `Confirmar Pedido${tipoPeriodo !== 'diario' ? 's' : ''}`
                    }
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'historial' && (
        <div className="card">
          <h2>Historial de Pedidos</h2>
          {pedidos.length === 0 ? (
            <div className="empty-state">
              <h3>No tienes pedidos anteriores</h3>
              <p>Aquí aparecerán todos tus pedidos realizados</p>
            </div>
          ) : (
            pedidos.map((pedido) => {
              const esFuturo = new Date(pedido.fecha) > new Date();
              const puedeCancelar = pedido.estado === 'confirmado' && esFuturo;

              return (
                <div key={pedido.id} className="pedido-card">
                  <div className="pedido-header">
                    <div className="pedido-fecha">
                      {new Date(pedido.fecha).toLocaleDateString('es-AR', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </div>
                    <span className={`estado-badge estado-${pedido.estado}`}>
                      {pedido.estado}
                    </span>
                  </div>
                  <div className="pedido-items">
                    <ul>
                      {pedido.items.map((item, idx) => (
                        <li key={idx}>
                          <strong>{item.categoria}:</strong> {item.plato}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {puedeCancelar && (
                    <button
                      className="btn btn-danger"
                      onClick={() => handleCancelarPedido(pedido.id)}
                      style={{ marginTop: '10px', width: 'auto', padding: '8px 16px' }}
                    >
                      Cancelar Pedido
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      </div>

      {showPerfil && (
        <PerfilUsuario
          usuario={usuario}
          onClose={() => setShowPerfil(false)}
        />
      )}
    </div>
  );
}

export default Dashboard;
